// Fetch-обёртка для admin SPA.
// Bridges:
//   - VITE_API_URL → baseUrl
//   - credentials: 'include' → cookies (httpOnly session)
//   - X-Tenant-ID header — из tenant-context (см. lib/tenant-context.tsx). Backend всё равно валидирует tenant из JWT, фронтовый header нужен для observability/routing на nginx.
//   - 4xx/5xx → бросаем типизированную ApiError, парсим RFC 7807 (Problem Details).
//   - Idempotency-Key: генератор для write-эндпоинтов (см. docs/stack/07-frontend-vite.md §4, docs/stack/08-pwa-offline.md §5).

import { trackApiError } from './track';

/** Один issue из Zod-валидации, как его отдаёт backend (ValidationError → errors.issues). */
export interface ProblemIssue {
  path?: Array<string | number>;
  message?: string;
  code?: string;
  [key: string]: unknown;
}

export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  /** Доменный machine-readable код (e.g. 'phone_already_taken'). */
  code?: string;
  detail?: string;
  instance?: string;
  /**
   * RFC 7807 extension. `issues` — для 422-валидации, но сюда же сервер кладёт
   * `details` любой доменной ошибки: у 429, например, `retry_after_seconds`
   * (см. DomainErrorFilter). Поэтому форма открытая, а не только issues.
   */
  errors?: { issues?: ProblemIssue[]; [key: string]: unknown };
  // Допускаем дополнительные поля по RFC 7807 §3.2.
  [key: string]: unknown;
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly problem: ProblemDetails;
  /** Доменный код ошибки (`problem.code`), если backend его передал. */
  public readonly code: string | undefined;
  /** Человекочитаемая деталь (`problem.detail`/`problem.title`). */
  public readonly detail: string | undefined;
  /** Issues валидации (422) — для маппинга на поля формы. */
  public readonly issues: ProblemIssue[];

  constructor(status: number, problem: ProblemDetails) {
    super(problem.detail ?? problem.title ?? `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
    this.code = typeof problem.code === 'string' ? problem.code : undefined;
    this.detail =
      typeof problem.detail === 'string'
        ? problem.detail
        : typeof problem.title === 'string'
          ? problem.title
          : undefined;
    this.issues = Array.isArray(problem.errors?.issues) ? problem.errors.issues : [];
  }
}

export interface ApiRequestOptions extends Omit<RequestInit, 'body' | 'headers'> {
  body?: unknown;
  headers?: Record<string, string>;
  idempotencyKey?: string;
  tenantId?: string | null;
}

const BASE_URL: string = import.meta.env.VITE_API_URL ?? '/api/v1';

const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Паузы повторов при недоступности сервера, мс. Сумма ~5 с — столько держится
 * окно, пока поднимается новый контейнер API (замерено на проде 27.08).
 */
const RETRY_DELAYS_MS = [500, 1500, 3000];

/**
 * POST, которые безопасно повторить. Вход и обновление сессии от повтора не
 * меняются: та же пара логин/пароль даёт ту же сессию. Всё остальное —
 * мутации, и повторять их нельзя (см. комментарий в apiFetch).
 */
const RETRYABLE_POSTS = new Set(['/auth/login', '/auth/refresh', '/auth/logout']);

/**
 * Генерирует Idempotency-Key для write-операций.
 * Для offline outbox — переопределяется явным ключом из `local_id`, см. docs/stack/08-pwa-offline.md §5.
 */
export function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // Fallback для старых браузеров.
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

async function parseProblem(response: Response): Promise<ProblemDetails> {
  const contentType = response.headers.get('content-type') ?? '';
  if (
    contentType.includes('application/problem+json') ||
    contentType.includes('application/json')
  ) {
    try {
      return (await response.json()) as ProblemDetails;
    } catch {
      // Пустое/невалидное тело — падаем на дефолт ниже.
    }
  }
  return {
    title: response.statusText || `HTTP ${response.status}`,
    status: response.status,
  };
}

export async function apiFetch<T = unknown>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const { body, headers: extraHeaders, idempotencyKey, tenantId, method, ...rest } = options;
  const url = path.startsWith('http') ? path : `${BASE_URL}${path}`;
  const httpMethod = (method ?? (body !== undefined ? 'POST' : 'GET')).toUpperCase();

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...extraHeaders,
  };

  if (body !== undefined && !(body instanceof FormData)) {
    headers['Content-Type'] = headers['Content-Type'] ?? 'application/json';
  }

  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId;
  }

  // Idempotency-Key обязателен для финансовых мутаций и markAttendance (см. PRD §16).
  if (WRITE_METHODS.has(httpMethod)) {
    headers['Idempotency-Key'] = idempotencyKey ?? generateIdempotencyKey();
  }

  const init: RequestInit = {
    ...rest,
    method: httpMethod,
    credentials: 'include',
    headers,
  };

  if (body !== undefined) {
    init.body = body instanceof FormData ? body : JSON.stringify(body);
  }

  let response = await fetch(url, init);

  // Ф6 плана 38 + план 110: 502/503/504 — почти всегда рестарт контейнера при
  // деплое. Раньше повтор был один и только для GET, поэтому ФОРМА ВХОДА
  // падала на первом же отказе: владелец 27.08 получил «HTTP 502» на экране
  // логина ровно в окно выката. Теперь повторов несколько, с нарастающими
  // паузами (сумма ~5 с покрывает замеренное окно), и вход в их числе.
  //
  // POST повторяем ТОЛЬКО из списка ниже: 502 не доказывает, что запрос не
  // выполнился, — nginx мог потерять уже готовый ответ, и повтор продажи создал
  // бы вторую (родня гочи про двойную проводку при обрыве сети).
  const canRetry = httpMethod === 'GET' || RETRYABLE_POSTS.has(path);
  if (canRetry) {
    for (const delay of RETRY_DELAYS_MS) {
      if (response.ok) break;
      if (response.status !== 502 && response.status !== 503 && response.status !== 504) break;
      await new Promise((resolve) => setTimeout(resolve, delay));
      response = await fetch(url, init);
    }
  }

  if (!response.ok) {
    const problem = await parseProblem(response);
    // Телеметрия ошибок API глазами пользователя (docs/service/37 §6.2) — единый
    // choke-point на все запросы (queries/mutations/прямые вызовы). Не для
    // /telemetry (иначе рекурсия при сбое приёмника).
    // password_change_required — не ошибка, а штатная блокировка до смены
    // временного пароля (Ф4 плана 38): не засоряем «Логи» ложными ошибками.
    if (!path.includes('/telemetry/') && problem.code !== 'password_change_required') {
      trackApiError(typeof problem.code === 'string' ? problem.code : undefined, response.status, {
        path,
        method: httpMethod,
      });
    }
    throw new ApiError(response.status, problem);
  }

  // 204 No Content и подобные — возвращаем undefined как T.
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return (await response.json()) as T;
  }
  return (await response.text()) as unknown as T;
}

/**
 * Безопасный текст ошибки для показа пользователю.
 * Для ApiError берём доменную деталь (`detail`/`title`), иначе — общий русский
 * текст. Никогда не отдаём «сырое» `.message`/stack: оно может протечь во
 * финанс/PII-разделах (имена эндпоинтов, тело запроса и т. п.).
 */
/**
 * Единый показ ошибки в UI: «русский текст (код)». Backend отдаёт detail уже на
 * русском (каталог ошибок packages/domain). Код в скобках — чтобы владелец слал
 * скриншот, а мы грепали серверный лог по коду (docs/service/22 §4).
 */
export function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    const text = e.detail ?? e.code ?? 'Произошла ошибка. Попробуйте ещё раз.';
    // Код в скобках только когда есть и русский текст, и код (иначе не дублируем).
    return e.code && e.detail ? `${text} (${e.code})` : text;
  }
  // Не-ApiError (сетевой сбой и т.п.) — общий текст; raw .message НЕ протекает.
  return 'Произошла ошибка. Попробуйте ещё раз.';
}

// Удобные шорткаты.
export const api = {
  get: <T>(path: string, options?: ApiRequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: ApiRequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: ApiRequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: ApiRequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: ApiRequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'DELETE' }),
};

/**
 * Разбор ошибки сохранения формы (план 108, Ф4). До канона это тело было
 * скопировано в 13 модалках справочников; различался только фолбэк-текст.
 *
 * 422 с issues раскладывается по полям (ключ — последний сегмент path, как
 * ставит его zod у strict-DTO), остальное — общий текст формы: detail от
 * сервера, иначе `fallback`, а не-ApiError — «сетевая ошибка».
 */
export function applyFormApiError(
  err: unknown,
  opts: {
    setErrors: (fe: Record<string, string | undefined>) => void;
    setFormError: (msg: string) => void;
    /** Текст, когда сервер не прислал detail, например «Ошибка сохранения зала». */
    fallback: string;
  },
): void {
  if (err instanceof ApiError) {
    if (err.problem.errors?.issues?.length) {
      const fe: Record<string, string | undefined> = {};
      for (const issue of err.problem.errors.issues) {
        const key = issue.path?.[issue.path.length - 1];
        if (typeof key === 'string') fe[key] = issue.message;
      }
      opts.setErrors(fe);
    }
    opts.setFormError(err.problem.detail ?? opts.fallback);
  } else {
    opts.setFormError('Сетевая ошибка. Попробуйте ещё раз.');
  }
}
